import React from "react";
import { TextField, IconButton, Stack, MenuItem, Select } from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";

/**
 * ExtraObjectsInput
 *
 * Renders a dynamic list of extra objects with name, price, and quantity using Formik for state management.
 * Selection is keyed off of object._id to ensure consistent lookups.
 *
 * Props:
 * - formik: Formik bag (values, setFieldValue)
 * - extraObjects: array of { _id, name, price }
 */
const ExtraObjectsInput = ({ formik, extraObjects }) => {
  // Add a new empty entry
  const handleAdd = () => {
    const newList = [
      ...formik.values.extraObjectsList,
      { id: "", name: "", price: "", number: 1 }
    ];
    formik.setFieldValue("extraObjectsList", newList);
  };

  // Remove an entry by index
  const handleRemove = (index) => {
    const newList = formik.values.extraObjectsList.filter((_, i) => i !== index);
    formik.setFieldValue("extraObjectsList", newList);
  };

  // When a select is changed, find the object by _id and populate name, price, and reset number
  const handleSelectChange = (index, selectedId) => {
    const selected = extraObjects.find((obj) => obj._id === selectedId);
    if (!selected) return;
    const newList = [...formik.values.extraObjectsList];
    newList[index] = {
      id: selected._id,
      name: selected.name,
      price: selected.price,
      number: 1
    };
    formik.setFieldValue("extraObjectsList", newList);
  };

  // Update only the number field
  const handleNumberChange = (index, value) => {
    const newList = [...formik.values.extraObjectsList];
    newList[index].number = parseInt(value, 10) || 1;
    formik.setFieldValue("extraObjectsList", newList);
  };

  return (
    <Stack spacing={2}>
      {formik.values.extraObjectsList.map((obj, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="center">
          {/* Select by _id for consistent lookup */}
          <Select
            value={obj.id || ""}
            onChange={(e) => handleSelectChange(index, e.target.value)}
            displayEmpty
            sx={{ width: "40%", bgcolor: "white", borderRadius: 2 }}
          >
            <MenuItem value="" disabled>
              Wähle eine Option
            </MenuItem>
            {extraObjects.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {option.name} – {option.price}€
              </MenuItem>
            ))}
          </Select>

          {/* Read-only price */}
          <TextField
            label="Preis"
            type="number"
            value={obj.price}
            InputProps={{ readOnly: true }}
            sx={{ width: "20%", bgcolor: "white", borderRadius: 2 }}
          />

          {/* Quantity with change handler */}
          <TextField
            label="Anzahl"
            type="number"
            value={obj.number}
            onChange={(e) => handleNumberChange(index, e.target.value)}
            sx={{ width: "20%", bgcolor: "white", borderRadius: 2 }}
          />

          {/* Remove button */}
          <IconButton onClick={() => handleRemove(index)} color="error">
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}

      {/* Add new extra object */}
      <IconButton sx={{ width: "12%" }} onClick={handleAdd} >
        <AddIcon />
        <span style={{ marginLeft: 4 }}>Zusatzoption</span>
      </IconButton>
    </Stack>
  );
};

export default ExtraObjectsInput;